import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Briefcase, Flame, PackageCheck, Phone, Target, Truck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  BarList,
  SectionCard,
  StatTile,
  TargetBar,
  fmtDate,
  fmtMoney,
  fmtNum,
  fmtPct,
} from "@/components/reports/ReportWidgets";
import type { StageRow } from "@/types/report";
import { DashboardTasksWidget } from "./DashboardTasksWidget";
import { TextileBlock, onTimeHint, type OrderKpis, type TextileSummary } from "./TextileBlock";
import { DonutPieChart } from "@/components/charts/DonutPieChart";
import { RadialTargetGauge } from "@/components/charts/RadialTargetGauge";

export interface SalesExecSummary {
  textile: TextileSummary;
  orders: OrderKpis;
  kpis: {
    revenue: number;
    revenue_prev: number;
    target_amount: number;
    target_achieved_pct: number | null;
    open_deals: number;
    pipeline_amount: number;
    conversion_pct: number;
    conversion_prev: number;
    tasks_overdue: number;
  };
  hot_leads: Array<{ id: string; name: string; company_name: string; phone: string; mobile: string; lead_status: string; score: number; next_follow_up_at: string }>;
  new_leads: Array<{ id: string; name: string; company_name: string; phone: string; mobile: string; source: string; assigned_at: string; created_on: string }>;
  pipeline_by_stage: StageRow[];
  closing_this_month: Array<{ id: string; name: string; amount: number; probability: number; expected_close_date: string; stage_name: string }>;
  my_quotes: Array<{ id: string; display_number: string; quote_number: string; quote_status: string; grand_total: number; valid_until: string; account_name: string }>;
  pool: { count: number; leads: Array<{ id: string; name: string; company_name: string; source: string; created_on: string }> };
}

const QUOTE_STATUS_LABEL: Record<string, string> = {
  DRAFT: "Draft",
  PENDING_APPROVAL: "Awaiting approval",
  APPROVED: "Approved — send it",
  SENT: "Sent — follow up",
};

function LeadRow({ name, company, phone, meta }: { name: string; company: string; phone?: string; meta: React.ReactNode }) {
  return (
    <li className="flex items-center gap-3 py-2 px-1">
      <span className="flex-1 min-w-0">
        <span className="block text-xs font-medium text-foreground truncate">{name || company}</span>
        <span className="block text-[11px] text-muted-foreground truncate">
          {name ? company : ""} {meta}
        </span>
      </span>
      {phone && (
        <a
          href={`tel:${phone}`}
          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-foreground hover:bg-accent"
          aria-label={`Call ${name || company}`}
        >
          <Phone className="size-3" aria-hidden /> Call
        </a>
      )}
    </li>
  );
}

export function SalesExecDashboard({ data }: { data: SalesExecSummary }) {
  const k = data.kpis;
  const o = data.orders;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const quotesByStatus = React.useMemo(() => {
    const counts: Record<string, { count: number; total: number }> = {};
    data.my_quotes.forEach((q) => {
      const st = q.quote_status || "OTHER";
      if (!counts[st]) counts[st] = { count: 0, total: 0 };
      counts[st].count += 1;
      counts[st].total += q.grand_total || 0;
    });
    return Object.entries(counts).map(([st, val]) => ({
      label: QUOTE_STATUS_LABEL[st] || st,
      value: val.total,
      sublabel: `${val.count} quotes`,
    }));
  }, [data.my_quotes]);

  return (
    <div className="space-y-5">
      <TextileBlock data={data.textile} scope="Your deals and orders" />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatTile
          label="My orders booked (this month)"
          value={fmtMoney(o.order_value)}
          rawValue={o.order_value}
          previous={o.order_value_prev}
          icon={<PackageCheck className="size-4" />}
          hint={<span>{fmtNum(o.orders_booked)} orders · {fmtMoney(o.dispatched_value)} dispatched</span>}
        />
        <StatTile
          label="My target"
          value={k.target_achieved_pct === null ? "—" : fmtPct(k.target_achieved_pct)}
          icon={<Target className="size-4" />}
          hint={k.target_amount ? <TargetBar achieved={k.revenue} target={k.target_amount} /> : <span>Not set yet</span>}
        />
        <StatTile
          label="My open enquiries"
          value={fmtNum(k.open_deals)}
          icon={<Briefcase className="size-4" />}
          hint={
            k.tasks_overdue > 0 ? (
              <span className="inline-flex items-center gap-1 text-foreground font-medium">
                <AlertTriangle className="size-3.5" aria-hidden /> {k.tasks_overdue} overdue tasks
              </span>
            ) : (
              <span>{fmtMoney(k.pipeline_amount)} · {fmtPct(k.conversion_pct)} lead conversion</span>
            )
          }
        />
        <StatTile
          label="My dispatch on time"
          value={o.on_time_pct === null ? "—" : fmtPct(o.on_time_pct)}
          icon={<Truck className="size-4" />}
          hint={<span>{onTimeHint(o)}</span>}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard title="Target pacing" description="Won value against your monthly target">
          <RadialTargetGauge
            achieved={k.revenue}
            target={k.target_amount}
            label="My Target"
            formatMoney={(v) => fmtMoney(v)}
            size={180}
          />
        </SectionCard>

        <SectionCard
          title="My pipeline by stage"
          description="Open deal value by stage"
          action={<Link to="/deals" className="text-xs text-primary hover:underline">My deals</Link>}
        >
          <DonutPieChart
            data={data.pipeline_by_stage.map((s) => ({
              label: s.stage_name,
              value: s.amount,
              sublabel: `${s.deals} deals`,
            }))}
            formatValue={(v) => fmtMoney(v)}
            centerValue={fmtMoney(k.pipeline_amount)}
            centerLabel="Pipeline"
            donut={true}
          />
        </SectionCard>

        <SectionCard
          title="Quote status breakdown"
          description="Distribution of active quotes"
          action={<Link to="/quotes" className="text-xs text-primary hover:underline">All quotes</Link>}
        >
          <DonutPieChart
            data={quotesByStatus}
            formatValue={(v) => fmtMoney(v)}
            centerValue={String(data.my_quotes.length)}
            centerLabel="Quotes"
            donut={true}
          />
        </SectionCard>
      </div>

      <DashboardTasksWidget />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard
          title="My hot leads"
          description="HOT priority or score 60+"
          action={<Flame className="size-4 text-muted-foreground" aria-hidden />}
        >
          {data.hot_leads.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3">No hot leads right now.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.hot_leads.map((l) => (
                <LeadRow
                  key={l.id}
                  name={l.name}
                  company={l.company_name}
                  phone={l.mobile || l.phone}
                  meta={<>· score {l.score}{l.next_follow_up_at ? ` · follow up ${fmtDate(l.next_follow_up_at)}` : ""}</>}
                />
              ))}
            </ul>
          )}
        </SectionCard>
        <SectionCard
          title="New leads to contact"
          description="Assigned to you, not contacted yet"
          action={<Link to="/deals" className="text-xs text-primary hover:underline">All opportunities</Link>}
        >
          {data.new_leads.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3">You've contacted every new lead.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.new_leads.map((l) => (
                <LeadRow
                  key={l.id}
                  name={l.name}
                  company={l.company_name}
                  phone={l.mobile || l.phone}
                  meta={<>· assigned {fmtDate(l.assigned_at || l.created_on)}</>}
                />
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Closing this month" description="Including deals past their expected date">
          {data.closing_this_month.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3">No deals expected to close this month.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.closing_this_month.map((d) => {
                const late = new Date(d.expected_close_date) < today;
                return (
                  <li key={d.id}>
                    <Link to={`/deals/${d.id}`} className="flex items-center gap-3 py-2 hover:bg-accent/50 rounded-md px-1">
                      <span className="flex-1 min-w-0">
                        <span className="block text-xs font-medium text-foreground truncate">{d.name}</span>
                        <span className="block text-[11px] text-muted-foreground truncate">
                          {d.stage_name} · {fmtDate(d.expected_close_date)}
                          {late && <span className="font-semibold text-foreground"> · past due</span>}
                        </span>
                      </span>
                      <span className="text-xs font-semibold tabular-nums text-foreground">{fmtMoney(d.amount)}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          title="My open quotes"
          action={<Link to="/quotes" className="text-xs text-primary hover:underline">All quotes</Link>}
        >
          {data.my_quotes.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3">No open quotes.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.my_quotes.map((q) => (
                <li key={q.id}>
                  <Link to={`/quotes/${q.id}`} className="flex items-center gap-3 py-2 hover:bg-accent/50 rounded-md px-1">
                    <span className="flex-1 min-w-0">
                      <span className="block text-xs font-medium text-foreground truncate">
                        {q.display_number || q.quote_number} · {q.account_name || "—"}
                      </span>
                      <span className="block text-[11px] text-muted-foreground">valid until {fmtDate(q.valid_until)}</span>
                    </span>
                    <Badge variant="outline" className="text-[10px]">{QUOTE_STATUS_LABEL[q.quote_status] || q.quote_status}</Badge>
                    <span className="text-xs font-semibold tabular-nums text-foreground">{fmtMoney(q.grand_total)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
        <SectionCard
          title="Unassigned pool"
          description={`${fmtNum(data.pool.count)} leads anyone can claim`}
          action={<Link to="/deals" className="text-xs text-primary hover:underline">Sales Pipeline</Link>}
        >
          {data.pool.leads.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3">The pool is empty.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.pool.leads.map((l) => (
                <LeadRow key={l.id} name={l.name} company={l.company_name} meta={<>· {l.source} · {fmtDate(l.created_on)}</>} />
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
  );
}
